import type { FormInstance } from 'antd'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { is711CrossBorder } from '@/bizComponent/RecipientAddressForm/utils'
import { ADDRESSMAP, I18N_PREFIX, NEED_SPLIT_NAME_COUNTRY, NOT_SPLIT_WITHOUT_FIELDS, SPLIT_WITHOUT_FIELDS } from '@/constant'
import { ARRIVAL_METHOD } from '@/constant/arrivalMethod'
import { postBatchVerify } from '@/services/apis/address'
import { decodeLanguageAndProvince } from '@/utils/address'
import type { ValidResultVo } from '@/services/apis/address/types'
import { useSelector } from '@/store'
import { SHIPMENT_PICKUP_TYPE } from '@/bizComponent/ServiceProvider/enum'
import type { IAllFields } from '@/utils'

type IVerifyAddressOptions = {
  recipientAddressVeriify?: boolean
  isReverse?: boolean
}

/**
 * 校验收发地址
 * @param form
 * @returns
 */
export function useVerifyAddressFn(form: FormInstance<IAllFields>, options?: IVerifyAddressOptions) {
  const finalOptions = Object.assign(
    {},
    {
      recipientAddressVeriify: true,
    },
    options
  )

  const { recipientAddressVeriify, isReverse } = finalOptions
  const [loading, setLoading] = useState<boolean>(false)
  const verifyAddressFormRef = useRef<() => Promise<never>>()
  const { t } = useTranslation()
  const filterSenderCountries = useSelector((state) => state.dictionary.filterSenderCountries)
  const filterArrivalCountries = useSelector((state) => state.dictionary.filterArrivalCountries)
  const senderCountryCodes = useMemo(() => filterSenderCountries.map((item) => item.countryCode), [filterSenderCountries])
  const arrivalCountryCodes = useMemo(() => filterArrivalCountries.map((item) => item.countryCode), [filterArrivalCountries])
  const countryDicsMap = useSelector((state) => state.dictionary.countryMapDics)
  const userCountryCode = useSelector((state) => state.user.info.countryCode)

  /* 校验地址表单数据 */
  const verifyAddressForm = useCallback(async () => {
    try {
      const value = form.getFieldsValue()

      const { arrival_method, departure_countryCode, arrival_countryCode, arrival_store_companyCode, provider_pickup_type } = value
      // for call from another place no only the steps
      if (!departure_countryCode || (recipientAddressVeriify && !arrival_countryCode)) return
      const isCrossBorder = departure_countryCode !== arrival_countryCode
      const departureCountryCode = value.departure_countryCode
      const departireProvince = value.departure_province
      const departureCity = value.departure_city
      const departureVerifyParams = {
        addressType: 1,
        mobile: value.departure_mobile,
        phone: value.departure_mobile,
        countryCode: departureCountryCode,
        province: decodeLanguageAndProvince(departireProvince)?.province || '',
        city: departureCity,
        district: value.departure_district,
        town: value.departure_town,
        address: value.departure_address,
        addressTwo: value.departure_addressTwo,
        zipCode: value.departure_zipCode,
        email: value.departure_email,
        name: value.departure_fullName,
        firstName: value.departure_firstName,
        lastName: value.departure_lastName,
      }

      // 收件地址和发货地址分开两次校验
      const arrivalCountryCode = value.arrival_countryCode
      const arrivalProvince = value.arrival_province
      const arrivalCity = value.arrival_city
      const arrivalVerifyParams = {
        addressType: 2,
        countryCode: arrivalCountryCode,
        mobile: value.arrival_mobile,
        phone: value.arrival_mobile,
        province: decodeLanguageAndProvince(arrivalProvince)?.province || '',
        city: arrivalCity,
        district: value.arrival_district,
        town: value.arrival_town,
        address: value.arrival_address,
        addressTwo: value.arrival_addressTwo,
        zipCode: value.arrival_zipCode,
        email: value.arrival_email,
        name: value.arrival_fullName,
        firstName: value.arrival_firstName,
        lastName: value.arrival_lastName,
      }

      setLoading(true)

      // 只有宅配，或者店配711跨境才需要校验发货地址
      const isVerifyRecipientAddress = arrival_method === ARRIVAL_METHOD.home || (arrival_method === ARRIVAL_METHOD.store && is711CrossBorder(arrival_store_companyCode))
      // 退货并且到店寄件则不校验发货地址了
      const isVerifyDepartureAddress = !(isReverse && provider_pickup_type === SHIPMENT_PICKUP_TYPE.dropOff)
      const verifyList = [isVerifyDepartureAddress ? departureVerifyParams : null, isVerifyRecipientAddress && recipientAddressVeriify ? arrivalVerifyParams : null].filter(Boolean)

      const { data } = await postBatchVerify({
        verifyList,
        crossBoarder: isCrossBorder,
      })

      const validResults: ValidResultVo[] = [].concat(...data.batchValidResults.map((item) => item.validResults))
      const isSplit = NEED_SPLIT_NAME_COUNTRY.includes(userCountryCode)

      if (validResults.length > 0) {
        const fields = validResults
          .map((item) => {
            // 把所有校验失败的输入框 setError
            // 如果fieldsNames 是发货人或者收货人，需要去判断是否拆分国家，如果是，则需要忽略

            const fieldName = item.fieldNames[0]
            if (isSplit && SPLIT_WITHOUT_FIELDS.includes(fieldName)) {
              return null
            }
            if (!isSplit && NOT_SPLIT_WITHOUT_FIELDS.includes(fieldName)) {
              return null
            }
            const name = ADDRESSMAP[fieldName]
            const targetValue = value[name]
            const errors = [t(I18N_PREFIX + item.messageCode, item?.messageData || {})]

            return {
              name,
              value: targetValue,
              errors,
            }
          })
          .filter(Boolean)

        if (fields.length > 0) {
          form.setFields(fields)
          return Promise.reject(fields)
        }
      }
    } catch (error) {
      return Promise.reject(error)
    } finally {
      setLoading(false)
    }
  }, [form, recipientAddressVeriify, isReverse, userCountryCode, t])

  verifyAddressFormRef.current = verifyAddressForm

  /** 校验收发货国家是否已授权 */
  const verifyAddressCountry = useCallback(
    (isPlatformLogisticsType: boolean) => {
      const value = form.getFieldsValue()
      const { departure_countryCode, arrival_countryCode } = value
      const isSenderInvalid = departure_countryCode && !senderCountryCodes?.includes(departure_countryCode)
      const isConsigneeInvalid = isPlatformLogisticsType ? false : arrival_countryCode && !arrivalCountryCodes?.includes(arrival_countryCode)

      const fields = []
      if (isSenderInvalid)
        fields.push({
          name: 'departure_countryCode',
          value: departure_countryCode,
          errors: [t('error.senderCountry', { senderCountry: countryDicsMap?.[departure_countryCode]?.countryName })],
        })
      if (isConsigneeInvalid)
        fields.push({
          name: 'arrival_countryCode',
          value: arrival_countryCode,
          errors: [t('error.consigneeCountry')],
        })
      if (fields.length) {
        form.setFields(fields)
        throw fields
      }
    },
    [form, senderCountryCodes, arrivalCountryCodes, t, countryDicsMap]
  )

  return { verifyAddressForm, verifyLoading: loading, verifyAddressFormRef, verifyAddressCountry }
}
